/* MIL-STD-2525D/E Symbol Identification Code — composed from parts, never hand-typed.
 *
 * A 20-digit SIDC hand-written as a string literal is unreviewable and silently wrong.
 * Composing it from named parts makes it correct by construction, and it mirrors how
 * Forge stores symbology: SIDC is derived from data, and rendering is a presentation
 * concern over it (SPEC-02 §3.2).
 *
 * Layout (2525D/E):
 *   [1-2] version  [3] context  [4] affiliation  [5-6] symbol set  [7] status
 *   [8] HQ/TF/dummy  [9-10] amplifier  [11-16] entity  [17-20] modifiers
 */

export const Affiliation = {
  pending: '0',
  unknown: '1',
  assumedFriend: '2',
  friend: '3',
  neutral: '4',
  suspect: '5',
  hostile: '6',
} as const
export type Affiliation = (typeof Affiliation)[keyof typeof Affiliation]

export const SymbolSet = {
  air: '01',
  airMissile: '02',
  space: '05',
  landUnit: '10',
  landCivilian: '11',
  landEquipment: '15',
  landInstallation: '20',
  seaSurface: '30',
  subsurface: '35',
} as const
export type SymbolSet = (typeof SymbolSet)[keyof typeof SymbolSet]

export const Status = {
  present: '0',
  planned: '1',
  fullyCapable: '2',
  damaged: '3',
  destroyed: '4',
} as const
export type Status = (typeof Status)[keyof typeof Status]

/** Entity codes, positions 11-16. Only the ones this prototype uses. */
export const Entity = {
  // Air (01)
  fixedWing: '110000',
  fighter: '110100',
  bomber: '110200',
  tanker: '110300',
  aew: '110600',
  patrolAir: '110700',
  cargoAir: '111000',
  rotary: '120000',
  uav: '130000',
  // Sea surface (30)
  combatantLine: '120400',
  carrier: '120100',
  surfaceCombatant: '120500',
  amphibiousWarfare: '120600',
  minewarfare: '120700',
  auxiliary: '130000',
  merchant: '180000',
  // Subsurface (35)
  submarine: '110000',
  submarineNuclear: '110100',
  // Land unit (10)
  airDefence: '130100',
  infantry: '121100',
  armour: '121000',
  artillery: '130300',
  // Land installation (20)
  airfield: '120200',
  navalBase: '120400',
  c2Node: '110100',
  // Space (05)
  satellite: '110000',
} as const

interface SidcParts {
  affiliation: Affiliation
  set: SymbolSet
  entity: string
  status?: Status
  /** Headquarters / task force / dummy indicator, position 8 */
  hq?: boolean
  /** Echelon or mobility amplifier, positions 9-10 */
  amplifier?: string
  /** Modifier 1 + 2, positions 17-20 */
  modifiers?: string
}

export function sidc({
  affiliation,
  set,
  entity,
  status = Status.present,
  hq = false,
  amplifier = '00',
  modifiers = '0000',
}: SidcParts): string {
  const code =
    '10' + // version 2525E
    '0' + // context: reality
    affiliation +
    set +
    status +
    (hq ? '1' : '0') +
    amplifier +
    entity.padEnd(6, '0') +
    modifiers

  if (code.length !== 20) {
    throw new Error(`SIDC must be 20 digits, produced ${code.length}: ${code}`)
  }
  return code
}

/** Affiliation → design token. Colour is never the only channel — 2525 frame shape carries it too. */
export function affiliationToken(a: Affiliation): string {
  switch (a) {
    case Affiliation.friend:
    case Affiliation.assumedFriend:
      return 'var(--color-friend)'
    case Affiliation.hostile:
    case Affiliation.suspect:
      return 'var(--color-hostile)'
    case Affiliation.neutral:
      return 'var(--color-neutral)'
    default:
      return 'var(--color-unknown)'
  }
}

export function affiliationLabel(a: Affiliation): string {
  const map: Record<string, string> = {
    '0': 'PENDING',
    '1': 'UNKNOWN',
    '2': 'ASSUMED FRIEND',
    '3': 'FRIEND',
    '4': 'NEUTRAL',
    '5': 'SUSPECT',
    '6': 'HOSTILE',
  }
  return map[a] ?? 'UNKNOWN'
}
